<script lang="ts">
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"

  import { buildCalendarBlockerCardTags } from "../../_shared/_logic/event-card-tags"
  import { resolveCalendarSyncBadge } from "../../_shared/_logic/calendar-sync-badge"
  import type { BoardEvent } from "../../../../../../../../../_logic/calendar-types"

  interface Props {
    item: BoardEvent
    employeeLabel?: string
    calendarSyncEnabled?: boolean
    showEventTooltips?: boolean
  }

  const {
    item,
    employeeLabel = "",
    calendarSyncEnabled = false,
    showEventTooltips = true,
  }: Props = $props()

  const blockerCardTags = $derived(buildCalendarBlockerCardTags({ item, employeeLabel }))
  const calendarSyncBadge = $derived(resolveCalendarSyncBadge(item.event, calendarSyncEnabled))
</script>

{#if blockerCardTags}
  <div class="event-tag-stack event-tag-stack--blocker">
    <div class="event-title event-title--blocker" title={item.title}>{item.title}</div>
    <div class="event-tag-row event-tag-row--blocker-time">
      <span class="calendar-status-tag-compact calendar-status-tag-compact--blocker">
        <StatusTag
          variant="neutral"
          class="calendar-status-tag calendar-blocker-time-tag"
          size="sm"
          label={blockerCardTags.time.label}
          subLabel={blockerCardTags.time.subLabel ?? undefined}
          color={blockerCardTags.time.color}
          icon={blockerCardTags.time.icon}
          tooltipContent={showEventTooltips ? blockerCardTags.time.tooltip : ""}
          tooltipHtml={true}
          tooltipClassName={blockerCardTags.time.tooltipClassName}
          disableTitle={true}
        />
      </span>
    </div>
    {#if calendarSyncBadge}
      <div class="event-tag-row event-tag-row--blocker-sync">
        <StatusTag
          class="calendar-provider-sync-tag calendar-provider-sync-tag--blocker"
          size="sm"
          label={calendarSyncBadge.label}
          color={calendarSyncBadge.color}
          icon={calendarSyncBadge.icon}
          tooltipContent={calendarSyncBadge.tooltip}
          tooltipHtml={true}
          disableTitle={true}
        />
      </div>
    {/if}
    {#if blockerCardTags.detailLines.length}
      <div class="event-raw-details">
        {#each blockerCardTags.detailLines as detailLine}
          <span>{detailLine}</span>
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style lang="less">
  .event-tag-stack {
    display: flex;
    flex: 0 0 auto;
    min-width: 0;
    max-width: 100%;
    flex-direction: column;
    gap: 0.16rem;
    overflow-x: hidden;
    overflow-y: visible;
  }

  .event-tag-stack--blocker {
    gap: 0.2rem;
  }

  .event-title {
    flex: 0 0 auto;
    font-weight: 800;
    font-size: 0.7rem;
    letter-spacing: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .event-title--blocker {
    color: var(--cal-text, #2f4858);
    font-size: 0.72rem;
    line-height: 1.12;
    white-space: nowrap;
  }

  .event-tag-row {
    display: flex;
    flex: 0 0 auto;
    min-width: 0;
    max-width: 100%;
    align-items: stretch;
    overflow-x: hidden;
    overflow-y: visible;
  }

  .event-tag-row--blocker-time,
  .event-tag-row--blocker-sync {
    align-items: flex-start;
  }

  .calendar-status-tag-compact {
    display: inline-flex;
    flex: 0 0 auto;
    min-width: 0;
    max-width: 100%;
    overflow-x: hidden;
    overflow-y: visible;
  }

  .event-tag-row :global(.status-tag),
  .calendar-status-tag-compact :global(.status-tag) {
    min-width: 0;
    max-width: 100%;
    cursor: inherit !important;
  }

  .event-tag-row :global(.status-tag *),
  .calendar-status-tag-compact :global(.status-tag *) {
    cursor: inherit !important;
  }

  .event-tag-row :global(.calendar-provider-sync-tag.status-tag .status-tag__label) {
    min-width: 0;
    max-width: 100%;
    padding: 0.16rem 0.24rem;
    font-size: 0.58rem;
    font-weight: 800;
  }

  .calendar-status-tag-compact :global(.status-tag__label) {
    min-width: 0;
    max-width: 100%;
    padding: 0.16rem 0.24rem;
    font-size: 0.58rem;
    font-weight: 800;
    gap: 0.04rem;
  }

  .calendar-status-tag-compact :global(.status-tag__main-label),
  .calendar-status-tag-compact :global(.status-tag__sub-label) {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .event-tag-row :global(.calendar-provider-sync-tag.status-tag .status-tag__icon),
  .calendar-status-tag-compact :global(.status-tag__icon) {
    flex: 0 0 1.25rem;
    width: 1.25rem;
    min-width: 1.25rem;
    max-width: 1.25rem;
    height: auto;
    min-height: 0;
    max-height: none;
    box-sizing: border-box;
    display: inline-flex;
    align-self: stretch;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .event-tag-row :global(.calendar-provider-sync-tag.status-tag .status-tag__icon svg),
  .calendar-status-tag-compact :global(.status-tag__icon svg) {
    width: 0.65rem;
    height: 0.65rem;
  }

  .event-raw-details {
    display: flex;
    flex: 0 0 auto;
    min-width: 0;
    flex-direction: column;
    gap: 0.08rem;
    color: var(--cal-text-muted, #6d97b0);
    font-size: 0.62rem;
    font-weight: 650;
    line-height: 1.18;
  }

  .event-raw-details span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
</style>
